import React, { useEffect, useMemo, useRef, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Typography } from "antd";
import dayjs from "dayjs";
import useNewsData from "../hooks/useNewsData";
import { isNewsFinished } from "../firebase-config";
import PageLoader from "./PageLoader";
import YouTubeEmbed from "./YouTubeEmbed";
import XpRewardSection, { parseXpReward } from "./XpRewardSection";
import InscripcionButton from "./InscripcionButton";

// Cada cuánto se revisa si alguna noticia llegó a su fecha de finalización.
const EXPIRY_CHECK_MS = 60 * 1000;

// Alto de referencia: lo que ocupa una noticia con video sin "Ver más".
const MAX_CARD_BODY_HEIGHT = 440;

// Convierte URLs dentro de un texto en enlaces clicables.
const renderTextWithLinks = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s<]+)/gi;
  const parts = text.split(urlRegex);
  return parts.map((part, index) =>
    /^https?:\/\//i.test(part) ? (
      <a
        key={index}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#7c5cfc", fontWeight: 700, wordBreak: "break-all" }}
      >
        {part}
      </a>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    ),
  );
};

const NewsCarousel: React.FC = () => {
  const { newsData, isLoading } = useNewsData();

  // ── Fecha de finalización (agregado 11/10/2026) ──
  // Las noticias vencidas no se muestran. "now" se actualiza cada minuto
  // para que una noticia desaparezca sola sin recargar la página.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), EXPIRY_CHECK_MS);
    return () => window.clearInterval(timer);
  }, []);
  const visibleNews = useMemo(
    () => newsData.filter((news) => !isNewsFinished(news.FechaFin, now)),
    [newsData, now],
  );

  // ── Video (agregado 06/10/2026) ──
  // Mientras un video se reproduce, el carrusel deja de avanzar solo.
  // Si el usuario cambia de noticia (flechas, puntos o swipe), el video
  // se detiene y el autoplay del carrusel se reanuda.
  const sliderRef = useRef<Slider>(null);
  const videoPlayingRef = useRef(false);
  const [stopSignal, setStopSignal] = useState(0);

  // Texto largo se colapsa; "Ver más" lo expande sin crecer de más las demás tarjetas.
  const bodyRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
  const [overflowingIds, setOverflowingIds] = useState<Record<string, boolean>>(
    {},
  );

  useEffect(() => {
    const nextOverflowing: Record<string, boolean> = {};
    visibleNews.forEach((news) => {
      const el = bodyRefs.current[news.id];
      if (el) {
        nextOverflowing[news.id] = el.scrollHeight > el.clientHeight + 4;
      }
    });
    setOverflowingIds(nextOverflowing);
  }, [visibleNews]);

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      if (next[id]) {
        sliderRef.current?.slickPause();
      } else if (!videoPlayingRef.current) {
        sliderRef.current?.slickPlay();
      }
      return next;
    });
  };

  const handleVideoPlay = () => {
    videoPlayingRef.current = true;
    sliderRef.current?.slickPause();
  };

  const handleBeforeChange = () => {
    // Al cambiar de noticia, la que quedó expandida vuelve a colapsarse.
    setExpandedIds({});
    videoPlayingRef.current = false;
    setStopSignal((value) => value + 1);
    sliderRef.current?.slickPlay();
  };

  if (isLoading && !visibleNews.length) {
    return <PageLoader minHeight={200} />;
  }

  if (!visibleNews.length) {
    return (
      <div style={{ padding: 16 }}>
        <Typography.Text style={{ color: "var(--dashboard-text)" }}>
          No hay noticias para mostrar.
        </Typography.Text>
      </div>
    );
  }

  return (
    <div className="news-carousel" style={{ padding: "16px 16px 8px" }}>
      <Slider
        ref={sliderRef}
        dots
        arrows={false}
        infinite
        speed={1000}
        autoplay
        autoplaySpeed={6500}
        pauseOnHover
        slidesToShow={1}
        slidesToScroll={1}
        beforeChange={handleBeforeChange}
      >
        {visibleNews.map((news) => (
          <div key={news.id} style={{ height: "100%" }}>
            <div
              style={{
                height: "100%",
                borderRadius: 18,
                padding: 16,
                background:
                  "linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 243, 250, 0.92) 100%)",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <div
                ref={(el) => {
                  bodyRefs.current[news.id] = el;
                }}
                style={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  maxHeight: expandedIds[news.id]
                    ? "none"
                    : MAX_CARD_BODY_HEIGHT,
                  overflow: "hidden",
                }}
              >
                {news.Imagen ? (
                  <div
                    style={{
                      height: 220,
                      borderRadius: 16,
                      overflow: "hidden",
                      border: "1px solid var(--dashboard-panel-border)",
                    }}
                  >
                    <img
                      src={news.Imagen}
                      alt={news.Titulo}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                ) : null}

                {news.Video ? (
                  <YouTubeEmbed
                    url={news.Video}
                    title={news.Titulo}
                    stopSignal={stopSignal}
                    onPlay={handleVideoPlay}
                    exp={parseXpReward(news.Exp)}
                  />
                ) : null}

                <div>
                  <Typography.Title
                    level={4}
                    style={{
                      margin: "0 0 4px",
                      color: "var(--dashboard-header-blue)",
                    }}
                  >
                    {news.Titulo}
                  </Typography.Title>

                  {news.Subtitle ? (
                    <Typography.Text
                      strong
                      style={{
                        display: "block",
                        marginBottom: 6,
                        color: "var(--dashboard-text)",
                      }}
                    >
                      {news.Subtitle}
                    </Typography.Text>
                  ) : null}

                  <Typography.Paragraph
                    style={{ margin: 0, color: "var(--dashboard-text)" }}
                  >
                    {renderTextWithLinks(news.Cuerpo)}
                  </Typography.Paragraph>

                  {news.FechaFin && dayjs(news.FechaFin).isValid() ? (
                    <Typography.Text
                      strong
                      style={{
                        display: "block",
                        marginTop: 8,
                        fontSize: 13,
                        color: "var(--dashboard-header-blue)",
                      }}
                    >
                      ⏳ Disponible hasta el{" "}
                      {dayjs(news.FechaFin).format("DD/MM/YYYY [a las] HH:mm")}
                    </Typography.Text>
                  ) : null}
                </div>

                {news.Exp ? (
                  <XpRewardSection
                    exp={news.Exp}
                    detail={news.ExpDetalle}
                    compact
                  />
                ) : null}

                {!expandedIds[news.id] && overflowingIds[news.id] ? (
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: 36,
                      background:
                        "linear-gradient(180deg, rgba(240,243,250,0) 0%, rgba(240,243,250,0.95) 85%)",
                    }}
                  />
                ) : null}
              </div>

              {overflowingIds[news.id] ? (
                <button
                  type="button"
                  onClick={() => toggleExpanded(news.id)}
                  style={{
                    alignSelf: "flex-start",
                    border: "none",
                    background: "none",
                    padding: 0,
                    color: "#7c5cfc",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {expandedIds[news.id] ? "Ver menos" : "Ver más"}
                </button>
              ) : null}

              {/* Inscripción opcional (11/10/2026). Va fuera del cuerpo
                  colapsable para que el botón siempre quede visible. */}
              {news.Inscripcion ? (
                <div style={{ marginTop: 4 }}>
                  <InscripcionButton
                    coleccion="Noticias"
                    itemId={news.id}
                    compact
                  />
                </div>
              ) : null}
            </div>
          </div>
        ))}
      </Slider>
    </div>
  );
};

export default NewsCarousel;
